import { RecentArticlesSection } from "@components/Blocks/RecentArticlesSection";
import { ArticleGridCards } from "@components/Cards/Layout/ArticleGridCards";
import MainContainer from "@components/Container/MainContainer";
import { AdsLb2, AdsLb3 } from "@elements/Advertisement/variant/AdsLb";
import PromoteEpaper from "@elements/Banner/PromoteEpaper/PromoteEpaper";
import { getGTMData } from "@helper/getMetaData";
import { colorConstants } from "@helper/sectionConstants";
import { capitalizeFirst } from "@helper/utils";
import { usePageNumberSync } from "@hooks/useLoadMore/usePageNumberSync";
import { Route } from "@route-types/Section/+types/Section";
import ErrorPage from "@routes/StatusHandler/StatusHandler";
import {
  fetchContext,
  ResponseType,
  type TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { createMetaTags, type MetaTagsProps } from "@sphtech/web2-seo-schema";
import { LOCALHOST } from "@src/app/config/MetaTagsConstants";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import type { GADataProps } from "@typings/PageMetaData";
import { SectionPayloadResponseType } from "@typings/PagePayloadType";
import React from "react";
import { ReactElement } from "react";
import { useMemo } from "react";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import fetchSectionData from "./Section.server";
import SectionInfiniteScroll from "./SectionInfiniteScroll";

export function meta(args: Route.MetaArgs) {
  const dataLoaderResponse = args.loaderData as TRouteWithRedirect<
    SectionPayloadResponseType,
    string
  >;
  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const metaData = dataLoaderResponse.payload.metaTags as MetaTagsProps;
    return createMetaTags(metaData);
  }
}

export default function Section(): ReactElement {
  const dataLoaderResponse: TRouteWithoutRedirect<
    SectionPayloadResponseType,
    string
  > = useLoaderData();
  const { setPageRef } = usePageNumberSync();

  const sectionArticles = dataLoaderResponse.type
    ? []
    : dataLoaderResponse.payload.data;
  const firstFiveArticles = sectionArticles.slice(0, 5);
  const remainingArticles = sectionArticles.slice(5);

  const loadedSections = useMemo(() => {
    // Split remaining articles into chunks of 8 for section rendering blocks.
    return remainingArticles.reduce<ProcessedArticleData[][]>(
      (sections, context, index) => {
        if (index % 8 === 0) sections.push([]);
        sections[sections.length - 1].push(context);
        return sections;
      },
      [],
    );
  }, [remainingArticles]);

  if (dataLoaderResponse.type) {
    return <ErrorPage response={dataLoaderResponse} />;
  }

  const sectionLevels = dataLoaderResponse.payload.sectionLevels;

  const sectionName = capitalizeFirst(sectionLevels[0] || "");
  const subSectionName = capitalizeFirst(sectionLevels[1] || "").replace(
    /[^A-Za-z0-9]+/g,
    "-",
  );
  const adSectionName = [sectionName, subSectionName].filter(Boolean).join("/");

  const epaperCoverImageUrl =
    dataLoaderResponse.payload.epaperCoverImage.coverImageUri;

  const sectionColor =
    sectionName in colorConstants
      ? colorConstants[sectionName as keyof typeof colorConstants].color.lightBg
      : "";

  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;

  return (
    <>
      <MainContainer
        pageAdTargetValue={pageAdTargetValue}
        sectionName={adSectionName}
      >
        {/* Load first 5 articles with stack cards layout */}
        <RecentArticlesSection
          recentArticles={firstFiveArticles}
          sectionName={sectionName}
          sectionColor={sectionColor}
          rootClassName="pb-5 px-3 xl:px-0 xmd:pb-0 max-w-7xl bg-white mx-auto block"
          hasShowAuthor={true}
        />
        {/* Load remaining articles with grid card layout */}
        {loadedSections.map((section, sectionIndex) => (
          <React.Fragment key={sectionIndex}>
            {sectionIndex == 0 ? (
              <AdsLb2
                sectionName={adSectionName}
                pageAdTargetType={pageAdTargetValue}
                rootClassName={
                  "my-10 mt-5 xmd:mt-10 bg-grey-950 relative w-full py-xxm pt-2.5 pb-5 md:px-2 md:py-xxs lg:pt-4 lg:pb-6 lg:px-0"
                }
                index={sectionIndex}
              />
            ) : (
              <AdsLb3
                sectionName={adSectionName}
                pageAdTargetType={pageAdTargetValue}
                rootClassName={
                  "my-10 mt-5 xmd:mt-10 bg-grey-950 relative w-full py-xxm pt-2.5 pb-5 md:px-2 md:py-xxs lg:pt-4 lg:pb-6 lg:px-0"
                }
                index={sectionIndex}
              />
            )}
            <div
              key={sectionIndex}
              id={`page-${sectionIndex + 1}`}
              ref={setPageRef(sectionIndex + 1)}
            >
              <ArticleGridCards
                article={section}
                articleIndex={sectionIndex}
                rootClassName="px-3 xl:p-0 gap-3 md:inline-grid md:grid-cols-4 xmd:inline-grid lg:grid lg:max-w-7xl bg-white m-auto"
                cardWrapper="gap-2 xmd:py-0 md:flex-col md:max-w-[210px] xmd:max-w-full"
                imageWrapperStyle="md:max-w-[171px] xmd:w-full"
                hasShowAuthor={true}
              />
            </div>
          </React.Fragment>
        ))}

        <PromoteEpaper
          className="relative w-full flex mt-10"
          epaperCoverImageUrl={epaperCoverImageUrl}
        />

        <SectionInfiniteScroll
          sectionArticles={sectionArticles}
          pageAdTargetValue={pageAdTargetValue}
          sectionColor={sectionColor}
          adSectionName={adSectionName}
          setPageRef={setPageRef}
          initialPageCount={loadedSections.length}
        />
      </MainContainer>
    </>
  );
}

export async function loader({ request }: Route.LoaderArgs) {
  // Parse URL with localhost base to handle both relative paths ("/section") and absolute URLs ("https://localhost:5174/section")
  const url = new URL(request.url, LOCALHOST);
  // Extract path segments and remove empty strings (e.g., "/news/singapore" becomes ["news", "singapore"])
  const sectionLevels: string[] = url.pathname.split("/").filter(Boolean);
  const data = await fetchSectionData(sectionLevels);

  const sectionName = capitalizeFirst(sectionLevels[0] || "");
  const subSectionName = capitalizeFirst(sectionLevels[1] || "").replace(
    /[^A-Za-z0-9]+/g,
    "-",
  );
  const pageTitle = subSectionName
    ? `${subSectionName} - ${sectionName}`
    : sectionName;

  const gtmData = getGTMData({
    title: `${pageTitle.toLowerCase().replace(/[\s-]+/g, "_")}_index`,
    level2: sectionName.toLowerCase(),
    chapter1: subSectionName && subSectionName.toLowerCase(),
    url: url.toString(),
  } as GADataProps);
  return {
    ...data,
    gtm: gtmData,
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<SectionPayloadResponseType | null, string>
  >;
}
